<script setup lang="ts">
import { Fireworks } from 'fireworks-js';
import { onBeforeUnmount, ref, watch } from 'vue';
import { eventCueSources, getSoundLabel } from '../features/oneTimeEvents';
import CButton from './primitives/CButton.vue';

const props = defineProps<{
  activeSoundName: string | null;
  canManageActiveSound: boolean;
  fireworksText: string | null;
  randomSelection: {
    roomName: string;
    selectedParticipantName: string;
  } | null;
}>();

const emit = defineEmits<{
  stopSound: [];
}>();

const fireworksLayer = ref<HTMLElement | null>(null);
let fireworks: Fireworks | null = null;
let fireworksAudio: HTMLAudioElement | null = null;
let fireworksCueHandle: number | null = null;

function stopFireworks(): void {
  if (fireworksCueHandle !== null) {
    window.clearTimeout(fireworksCueHandle);
    fireworksCueHandle = null;
  }

  fireworksAudio?.pause();
  fireworksAudio = null;
  fireworks?.stop(true);
  fireworks = null;
}

function startFireworks(): void {
  stopFireworks();

  if (!fireworksLayer.value) {
    return;
  }

  fireworks = new Fireworks(fireworksLayer.value, {
    intensity: 30,
    opacity: 0.5,
    particles: 100,
  });
  fireworks.start();

  fireworksCueHandle = window.setTimeout(() => {
    fireworksCueHandle = null;
    fireworksAudio = new Audio(eventCueSources.fireworks);
    fireworksAudio.currentTime = 0;
    void fireworksAudio.play().catch(() => undefined);
  }, 500);
}

watch(
  () => props.fireworksText,
  (text) => {
    if (text) {
      startFireworks();
      return;
    }

    stopFireworks();
  },
  { flush: 'post' },
);

onBeforeUnmount(() => {
  stopFireworks();
});
</script>

<template>
  <div class="pointer-events-none fixed inset-0 z-70 overflow-hidden">
    <div
      v-if="fireworksText"
      ref="fireworksLayer"
      aria-hidden="true"
      class="fireworks-layer absolute inset-0"
      data-testid="fireworks-canvas-layer"
    />

    <div v-if="fireworksText" class="absolute inset-x-0 top-[45%] flex -translate-y-1/2 justify-center px-4">
      <div
        class="fireworks-text max-w-2xl px-8 py-6 text-center text-[40px] leading-tight font-semibold text-red-600"
        data-testid="fireworks-banner"
      >
        {{ fireworksText }}
      </div>
    </div>

    <div v-if="randomSelection" class="absolute top-40 right-5 max-w-sm px-4">
      <div
        class="rounded-4xl border border-emerald-200 bg-white/95 px-5 py-5 shadow-[0_22px_55px_rgba(5,150,105,0.18)] backdrop-blur"
        data-testid="random-selection-banner"
      >
        <p class="text-xs font-semibold tracking-[0.22em] text-emerald-700 uppercase">Random selection</p>
        <p class="mt-2 text-2xl font-semibold text-slate-950">{{ randomSelection.selectedParticipantName }}</p>
        <p class="mt-2 text-sm text-slate-700">Selected from {{ randomSelection.roomName }}.</p>
      </div>
    </div>

    <div
      v-if="activeSoundName && canManageActiveSound"
      class="pointer-events-auto absolute right-5 bottom-5 w-full max-w-62 px-3"
    >
      <div
        class="rounded-3xl border border-fuchsia-200 bg-white/95 px-4 py-3 shadow-[0_14px_36px_rgba(192,38,211,0.16)] backdrop-blur"
        data-testid="sound-effect-banner"
      >
        <p class="text-[10px] font-semibold tracking-[0.22em] text-fuchsia-700 uppercase">Room sound</p>
        <p class="mt-1 text-sm leading-snug font-semibold text-slate-950">{{ getSoundLabel(activeSoundName) }}</p>
        <div class="mt-2">
          <CButton block size="sm" tone="danger" data-testid="sound-effect-banner-stop" @click="emit('stopSound')">
            Stop playback
          </CButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fireworks-layer :deep(canvas) {
  height: 100% !important;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100% !important;
}

.fireworks-text {
  animation: fireworks-text-blink 1s step-end infinite;
  text-shadow: 0 2px 12px rgba(255, 255, 255, 0.9);
  overflow-wrap: anywhere;
}

@keyframes fireworks-text-blink {
  50% {
    opacity: 0;
  }
}
</style>
